import { makeAutoObservable, runInAction, reaction } from 'mobx'
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore'
import FirebaseApp from '../../../shared/api/firebase'
import {
  eachDayOfInterval,
  format,
  startOfMonth,
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  endOfMonth,
} from 'date-fns'

const db = getFirestore(FirebaseApp)

// Вспомогательная функция для расчета процентного изменения
const calculateDiff = (current, previous) => {
  if (
    previous === null ||
    previous === undefined ||
    current === null ||
    current === undefined
  ) {
    return null
  }
  if (previous === 0) {
    return current > 0 ? 100 : 0
  }
  return Math.round(((current - previous) / previous) * 100)
}

class DashboardStore {
  // Состояния для дашборда
  dashboardStats = null
  upcomingBookings = []
  isDashboardLoading = true // По умолчанию true, чтобы показать лоадер при первой загрузке
  visitsForChart = []
  revenueForChart = []
  popularServices = []
  isVisitsChartLoading = false
  isRevenueChartLoading = false

  constructor(rootStore) {
    makeAutoObservable(this, { rootStore: false }, { autoBind: true })
    this.rootStore = rootStore
    this.organizationStore = rootStore.organizationStore
    this.errorStore = rootStore.errorStore
    this.clientsStore = rootStore.clientsStore
    this.servicesStore = rootStore.servicesStore

    reaction(
      () => ({
        // Ждем завершения загрузки клиентов и услуг, а не просто их инициализации.
        clientsLoading: this.clientsStore.isLoading,
        servicesLoading: this.servicesStore.isLoading,
        orgId: this.organizationStore.organization?.id,
      }),
      ({ orgId, clientsLoading, servicesLoading }) => {
        // Запускаем инициализацию, когда ID организации есть и загрузка зависимых сторов завершена.
        if (orgId && !clientsLoading && !servicesLoading) {
          this.initializeDashboard()
        }
      },
      { fireImmediately: true },
    )
  }

  // Новый метод для инициализации всех данных дашборда
  async initializeDashboard() {
    // Флаг isDashboardLoading уже true, нет нужды устанавливать его снова.
    try {
      // Параллельно загружаем все необходимые данные
      await Promise.all([
        this.fetchDashboardData(),
        this.fetchPopularServices(),
      ])
    } catch (error) {
      // Ошибки логируются и отображаются в конкретных fetch-методах,
      // здесь просто ловим, чтобы finally выполнился корректно.
      console.error('Dashboard initialization failed:', error)
    } finally {
      runInAction(() => {
        this.isDashboardLoading = false
      })
    }
  }

  async fetchDashboardData(staffId = 'all') {
    const orgId = this.organizationStore.organization?.id
    if (!orgId) {
      return
    }

    try {
      const now = new Date()
      const todayStart = startOfDay(now)
      const todayEnd = endOfDay(now)
      const monthStart = startOfMonth(now)

      // Даты для сравнения
      const yesterday = subDays(now, 1)
      const yesterdayStart = startOfDay(yesterday)
      const yesterdayEnd = endOfDay(yesterday)
      const prevMonthStart = startOfMonth(subMonths(now, 1))
      const prevMonthEnd = endOfMonth(subMonths(now, 1))

      const bookingsColRef = collection(db, 'organizations', orgId, 'bookings')
      const clientsColRef = collection(db, 'organizations', orgId, 'clients')

      let todayQuery = query(
        bookingsColRef,
        where('startTime', '>=', todayStart),
        where('startTime', '<=', todayEnd),
      )
      let upcomingQuery = query(
        bookingsColRef,
        where('startTime', '>', now),
        orderBy('startTime'),
        limit(5),
      )
      const newClientsQuery = query(
        // New clients are not staff-specific
        clientsColRef,
        where('createdAt', '>=', monthStart),
      )

      // Запросы для предыдущего периода
      let yesterdayQuery = query(
        bookingsColRef,
        where('startTime', '>=', yesterdayStart),
        where('startTime', '<=', yesterdayEnd),
      )
      const prevMonthNewClientsQuery = query(
        // New clients are not staff-specific
        clientsColRef,
        where('createdAt', '>=', prevMonthStart),
        where('createdAt', '<=', prevMonthEnd),
      )

      // Apply staff filter if a specific staff member is selected
      if (staffId && staffId !== 'all') {
        todayQuery = query(todayQuery, where('staffId', '==', staffId))
        upcomingQuery = query(upcomingQuery, where('staffId', '==', staffId))
        yesterdayQuery = query(yesterdayQuery, where('staffId', '==', staffId))
      }

      const [
        todaySnapshot,
        upcomingSnapshot,
        newClientsSnapshot,
        yesterdaySnapshot,
        prevMonthNewClientsSnapshot,
      ] = await Promise.all([
        getDocs(todayQuery),
        getDocs(upcomingQuery),
        getDocs(newClientsQuery),
        getDocs(yesterdayQuery),
        getDocs(prevMonthNewClientsQuery),
      ])

      runInAction(() => {
        const todayBookings = todaySnapshot.docs.map((d) => d.data())
        const yesterdayBookings = yesterdaySnapshot.docs.map((d) => d.data())

        const todayRevenue = todayBookings.reduce(
          (sum, b) => sum + (b.totalPrice || 0),
          0,
        )
        const yesterdayRevenue = yesterdayBookings.reduce(
          (sum, b) => sum + (b.totalPrice || 0),
          0,
        )

        this.dashboardStats = {
          todayBookingsCount: todayBookings.length,
          todayRevenue,
          newClientsThisMonth: newClientsSnapshot.size,
          // Считаем динамику
          bookingsDiff: calculateDiff(
            todayBookings.length,
            yesterdayBookings.length,
          ),
          revenueDiff: calculateDiff(todayRevenue, yesterdayRevenue),
          clientsDiff: calculateDiff(
            newClientsSnapshot.size,
            prevMonthNewClientsSnapshot.size,
          ),
        }

        this.upcomingBookings = upcomingSnapshot.docs.map((doc) => {
          const booking = { id: doc.id, ...doc.data() }
          return {
            ...booking,
            clientName:
              this.clientsStore.clientsById[booking.clientId]?.name || 'Клиент',
            serviceName:
              this.servicesStore.servicesById[booking.serviceIds?.[0]]?.name ||
              'Услуга',
            price: booking.totalPrice || 0,
            dateTime: booking.startTime.toDate(),
          }
        })
      })
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      this.errorStore.showError('Не удалось загрузить данные для дашборда.')
      throw error // Пробрасываем ошибку выше, чтобы Promise.all ее поймал
    }
  }

  async fetchVisitsForChart(period = 'week', staffId = 'all') {
    this.isVisitsChartLoading = true
    const orgId = this.organizationStore.organization?.id
    if (!orgId) {
      this.isVisitsChartLoading = false
      return
    }

    const today = new Date()
    const interval = {
      start: subDays(today, period === 'week' ? 6 : 29),
      end: today,
    }

    const days = eachDayOfInterval(interval)

    try {
      const promises = days.map(async (day) => {
        const start = startOfDay(day)
        const end = endOfDay(day)
        const bookingsColRef = collection(
          db,
          'organizations',
          orgId,
          'bookings',
        )
        let q = query(
          bookingsColRef,
          where('startTime', '>=', start),
          where('startTime', '<=', end),
        )

        // Apply staff filter if a specific staff member is selected
        if (staffId && staffId !== 'all') {
          q = query(q, where('staffId', '==', staffId))
        }

        const snapshot = await getDocs(q)
        return { date: day, visits: snapshot.size }
      })

      const results = await Promise.all(promises)
      runInAction(() => {
        this.visitsForChart = results.map((result) => ({
          name: format(result.date, 'dd/MM'),
          visits: result.visits,
        }))
      })
    } catch (error) {
      console.error('Failed to fetch visits for chart:', error)
      this.errorStore.showError(
        'Не удалось загрузить данные для графика посещений.',
      )
    } finally {
      runInAction(() => {
        this.isVisitsChartLoading = false
      })
    }
  }

  async fetchRevenueForChart(period = 'week', staffId = 'all') {
    this.isRevenueChartLoading = true
    const orgId = this.organizationStore.organization?.id
    if (!orgId) {
      this.isRevenueChartLoading = false
      return
    }

    const today = new Date()
    const interval = {
      start: subDays(today, period === 'week' ? 6 : 29),
      end: today,
    }

    const days = eachDayOfInterval(interval)

    try {
      const promises = days.map(async (day) => {
        const start = startOfDay(day)
        const end = endOfDay(day)
        const bookingsColRef = collection(
          db,
          'organizations',
          orgId,
          'bookings',
        )
        let q = query(
          bookingsColRef,
          where('startTime', '>=', start),
          where('startTime', '<=', end),
        )

        // Apply staff filter if a specific staff member is selected
        if (staffId && staffId !== 'all') {
          q = query(q, where('staffId', '==', staffId))
        }

        const snapshot = await getDocs(q)
        const dailyRevenue = snapshot.docs.reduce(
          (sum, doc) => sum + (doc.data().totalPrice || 0),
          0,
        )
        return { date: day, revenue: dailyRevenue }
      })

      const results = await Promise.all(promises)
      runInAction(() => {
        this.revenueForChart = results.map((result) => ({
          name: format(result.date, 'dd/MM'),
          revenue: result.revenue,
        }))
      })
    } catch (error) {
      console.error('Failed to fetch revenue for chart:', error)
      this.errorStore.showError(
        'Не удалось загрузить данные для графика доходов.',
      )
    } finally {
      runInAction(() => {
        this.isRevenueChartLoading = false
      })
    }
  }

  // ... (пропускаем fetchVisitsForChart и fetchRevenueForChart для краткости)

  async fetchPopularServices(staffId = 'all') {
    const orgId = this.organizationStore.organization?.id
    if (!orgId) return

    const monthStart = startOfMonth(new Date())

    const bookingsColRef = collection(db, 'organizations', orgId, 'bookings')
    let q = query(bookingsColRef, where('startTime', '>=', monthStart))

    if (staffId && staffId !== 'all') {
      q = query(q, where('staffId', '==', staffId))
    }
    const snapshot = await getDocs(q)

    const serviceCounts = snapshot.docs.reduce((acc, doc) => {
      const booking = doc.data()
      if (booking.serviceIds) {
        booking.serviceIds.forEach((serviceId) => {
          acc[serviceId] = (acc[serviceId] || 0) + 1
        })
      }
      return acc
    }, {})

    const sortedServices = Object.entries(serviceCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([id, count]) => {
        const service = this.servicesStore.servicesById[id]
        // Используем service?.name вместо service?.name?.ru, так как имя, скорее всего, является строкой
        return { id, name: service?.name || 'Неизвестная услуга', count }
      })

    runInAction(() => {
      this.popularServices = sortedServices
    })
  }
}

export default DashboardStore
